import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BubbleAlert, BubbleButton } from '@go-client/components/BubbleUI';
import {
  BubbleFormActions,
  BubbleFormFooter,
  BubbleFormHeader,
  FormPageComponentHelpfulProps,
} from '@go-client/components/BubbleUI/BubbleForm';
import { FormGenericContainer } from '@go-client/components/FormGenericContainer';
import {
  eventFormikValuesToNewEvent,
  EventTemplateFormikForm,
} from '@go-client/components/Formik/EventTemplateFormik';
import ReservableScheduleFormik, {
  ReservableScheduleFormikForm,
} from '@go-client/components/Formik/ReservableScheduleFormik';
import { User } from '@go-shared/types/user';
import { BareVenueTemplate } from '@go-shared/types/venueTemplate';
import { FormikProps } from 'formik';
import React, { useState } from 'react';
import { RESERVABLE_SCHEDULE_BREADCRUMBS } from '../ReservableSchedulePath';
import {
  rruleFromScheduleRule,
  rruleToText,
  splitRuleTextToMultiLines,
} from '@go-shared/lib/rrule';
import { mapBy } from '@go-shared/lib/functional';
import { makeFullName } from '@go-shared/lib/utils';
import { createDefaultRule, snapshotFromEventTemplate } from '@go-shared/lib/reserve';
import { useBubbleDrawerTools } from '@go-client/components/BubbleUI/BubbleDrawer';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { RESERVABLE_EXPERIENCE_TYPE } from '@go-shared/types/reservable';
import { canManageAllSchedules, canManageScheduleRule } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';

interface EnterSchedulePagePropsBase {
  isBusy: boolean;
  setIsBusy: (isBusy: boolean) => void;
  scheduleLocked: boolean;
  eventFormik: FormikProps<EventTemplateFormikForm>;
  scheduleFormik: FormikProps<ReservableScheduleFormikForm>;
  users: User[];
  venueTemplates: BareVenueTemplate[];
  experienceType: RESERVABLE_EXPERIENCE_TYPE;
}

type EnterSchedulePageProps = EnterSchedulePagePropsBase &
  FormPageComponentHelpfulProps<RESERVABLE_SCHEDULE_BREADCRUMBS>;

export default function EnterSchedulePage(props: EnterSchedulePageProps) {
  const forwardDisabled = !props.scheduleFormik.isValid || !props.eventFormik.isValid;

  const { activeProperty: property } = useActiveProperty();
  const { scheduleFormik, users, venueTemplates } = props;
  const { values, setFieldValue } = scheduleFormik;
  const { user } = useUserInfo();

  const { scrollToTop } = useBubbleDrawerTools();
  const usersDict = mapBy(users, '_id');

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({
    // [values.rules[0]?._id || '']: true,
  });

  function addNewRule() {
    const newExpanded = { ...expanded };
    const newRule = createDefaultRule();

    values.rules.forEach(r => {
      newExpanded[r._id] = false;
    });
    newExpanded[newRule._id] = true;

    if (!canManageAllSchedules(user)) {
      newRule.host = user._id;
    }

    setExpanded(newExpanded);
    setFieldValue('rules', [newRule, ...values.rules]);
    scrollToTop();
  }

  return (
    <div className="eaf-1">
      <FormGenericContainer
        className="h-screen flex flex-col"
        bodyClassName="flex-grow flex flex-col"
        footer={() => (
          <>
            <BubbleFormFooter
              className="text-slate-dark mr-auto"
              items={[{ label: 'Experience Selected', value: props.eventFormik.values.title }]}
            />
            <div className="justify-items-end">
              <BubbleFormActions
                isBusy={props.isBusy}
                forwardTitle={props.forwardTitle || 'Finish: Create Experience'}
                backTitle={props.backTitle}
                onGoForward={props.onGoForward}
                onGoBack={props.onGoBack}
                forwardDisabled={forwardDisabled}
              />
            </div>
          </>
        )}
      >
        <BubbleFormHeader<RESERVABLE_SCHEDULE_BREADCRUMBS>
          title={props.title || 'Create Experience'}
          subtitle={props.subtitle || 'Set Schedules'}
          pageIndex={props.pageIndex ?? 1}
          onClose={props.onClose}
          activeBreadcrumb="schedule"
          breadcrumbs={props.breadcrumbs}
          onBreadcrumbClick={props.onBreadcrumbClick}
        />
        {props.scheduleLocked && (
          <BubbleAlert className="mb-3" variant="ocean">
            <span className="font-semibold">This schedule cannot be modified. </span>
            You may not be a host for any of the schedule rules or you lack reserve admin
            permissions.
          </BubbleAlert>
        )}
        <div className="grid grid-cols-1 gap-2">
          {values.rules.length < 1 && (
            <div
              className="p-8 text-center min-h-56 flex flex-col items-center justify-center border border-smoke-lightest rounded-lg"
              style={{ borderStyle: 'dashed', borderSpacing: '1rem', borderWidth: '3px' }}
            >
              <div className="text-center grid grid-cols-1 gap-4">
                <div>
                  <div className="text-lg font-semibold text-slate mb-1">
                    Schedule list is empty
                  </div>
                  <div className="text-smoke">Add schedules to this experience</div>
                </div>
                {canManageAllSchedules(user) && (
                  <BubbleButton
                    icon="plus"
                    variant="navy"
                    onClick={addNewRule}
                    className="border border-navy min-w-0"
                    style={{ justifySelf: 'center' }}
                  >
                    Add your First Schedule
                  </BubbleButton>
                )}
              </div>
            </div>
          )}
          {values.rules.length > 0 && canManageAllSchedules(user) && (
            <BubbleButton
              icon="plus"
              variant="navy"
              block
              inverse
              onClick={addNewRule}
              className="border border-navy"
            >
              Add Another Schedule
            </BubbleButton>
          )}
          {values.rules.map((rule, i) => {
            const ruleText = rruleToText(rruleFromScheduleRule(rule.ruleFragments));
            const ruleLines = splitRuleTextToMultiLines(ruleText);
            const host = rule.host ? usersDict[rule.host] : undefined;
            const canEditRule = canManageScheduleRule(user, rule);

            return (
              <div key={rule._id} className="fragment-block">
                <div className="fb-header relative border-b">
                  <div className="flex items-center py-4 pl-6 pr-4">
                    <div
                      className={`flex-grow relative flex items-center ${rule.hidden ? 'text-smoke italic' : 'text-navy'}`}
                    >
                      <FontAwesomeIcon
                        icon={rule.ruleFragments.frequency === 'NEVER' ? 'calendar-day' : 'repeat'}
                        className="text-xl ml-2 mr-6"
                      />
                      <div className="py-1">
                        <div className="text-lg font-semibold">
                          {!canEditRule && (
                            <FontAwesomeIcon icon="lock" className="mr-2 text-crimson" />
                          )}
                          {ruleLines[0] || 'Schedule'}
                        </div>
                        <div
                          className={`text-xs flex items-center space-x-2 ${rule.hidden ? 'text-smoke' : 'text-slate'}`}
                        >
                          <span>{rule.slots.length} Slots</span>
                          {props.experienceType === 'resource' && (
                            <>
                              <FontAwesomeIcon icon="pipe" />
                              <span>{`${!rule.resources || rule.resources.length < 1 ? 'All' : rule.resources.length} Resources`}</span>
                            </>
                          )}
                          <FontAwesomeIcon icon="pipe" />
                          <span>
                            {host
                              ? `Hosted by ${makeFullName(host.firstName, host.lastName)}`
                              : 'No Host'}
                          </span>
                        </div>
                      </div>
                      <BubbleButton
                        className="ml-auto stretched-link p-3-i"
                        onClick={() => {
                          setExpanded({
                            ...expanded,
                            [rule._id]: !expanded[rule._id],
                          });
                        }}
                        variant="navy"
                        inverse
                        icon="edit"
                      />
                    </div>
                    {canEditRule && (
                      <>
                        <BubbleButton
                          className="p-3-i"
                          onClick={() => {
                            setFieldValue(`rules[${i}].hidden`, !rule.hidden);
                          }}
                          variant={rule.hidden ? 'smoke' : 'navy'}
                          inverse
                          icon={rule.hidden ? 'eye-slash' : 'eye'}
                        />
                        <div>
                          <BubbleButton
                            className="p-3-i"
                            onClick={() => {
                              if (i === 0) return;
                              const newRules = [...values.rules];
                              const temp = newRules[i - 1];
                              newRules[i - 1] = newRules[i];
                              newRules[i] = temp;
                              setFieldValue('rules', newRules);
                            }}
                            variant="slate"
                            inverse
                            icon="arrow-up"
                            disabled={i === 0}
                          />
                          <BubbleButton
                            className="p-3-i"
                            onClick={() => {
                              if (i === values.rules.length - 1) return;
                              const newRules = [...values.rules];
                              const temp = newRules[i];
                              newRules[i] = newRules[i + 1];
                              newRules[i + 1] = temp;
                              setFieldValue('rules', newRules);
                            }}
                            variant="slate"
                            inverse
                            icon="arrow-down"
                            disabled={i === values.rules.length - 1}
                          />
                          <BubbleButton
                            className="p-3-i"
                            onClick={() => {
                              const newRules = [...values.rules];
                              newRules.splice(i, 1);
                              setFieldValue('rules', newRules);
                            }}
                            variant="crimson"
                            inverse
                            icon="trash-alt"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {expanded[rule._id] && (
                  <div className="p-6">
                    <ReservableScheduleFormik
                      type={!canEditRule ? 'view' : 'edit'}
                      disableHost={!canManageAllSchedules(user)}
                      formik={scheduleFormik}
                      ruleId={rule._id}
                      users={users}
                      experienceType={props.experienceType}
                      venueTemplates={venueTemplates}
                      eventSnapshot={snapshotFromEventTemplate(
                        eventFormikValuesToNewEvent(props.eventFormik.values, property, undefined)
                      )}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </FormGenericContainer>
    </div>
  );
}
